import { IDropdownOption } from '@ui-modules/types';

export enum BookingTripType {
  OneWay = 'one-way',
  RoundTrip = 'round-trip',
  MultiLeg = 'multi-leg',
}

export enum BookingForwardType {
  Forward = 'forward',
  ForwardByOtherAgency = 'forwarded_by_other_agency',
  Forwarded = 'forwarded',
  NotForForward = 'not_for_forward',
  ProcessedByOtherAgency = 'processed_by_other_agency',
  Rejected = 'rejected',
  RejectedByAllFP = 'rejected_by_all_fp',
}

export type BookingState =
  | 'accepted'
  | 'cancelled'
  | 'cancelled_by_fp'
  | 'cancelled_by_user'
  | 'disabled'
  | 'not_processed'
  | 'rejected';
export type TransferType = 'airport' | 'in_town' | 'out_of_town';
export type TypeOfTrip = 'one-way' | 'round-trip' | 'multi-leg';

interface IBookingAgencyApi {
  agency_id_on_hbh: number;
  complete_name: string;
  domain: string;
  short_name: string;
  uuid: string;
}

interface IBookingAttachmentsApi {
  attachment_link: string;
  is_passport: boolean;
}

export interface IBookingDetailsApi {
  agency: string;
  agency_fk: IBookingAgencyApi | null;
  attachments: IBookingAttachmentsApi[];
  booking_agent_email: string;
  booking_agent_first_name: string;
  booking_agent_last_name: string;
  booking_agent_username: string;
  dispatch_date: string;
  drives: IBookingDriveApi[];
  email: string;
  facility_id: string;
  first_name: string;
  flight_arrival_departure_time: string;
  flight_number: string;
  forwarded: boolean;
  index_number: string;
  last_name: string;
  manager_email: string;
  n_of_passengers: number;
  passengers_info: {
    agency: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    requesting_unit_display: string;
  }[];
  phone_number: string;
  purpose: string;
  purpose_display: string;
  recurring: {
    end_date: string;
    frequency_display: string;
    start_date: string;
    weekdays: number[];
    weekdays_display: string;
  } | null;
  remarks: string;
  requesting_unit: string;
  requesting_unit_display: string;
  routes: IBookingRouteApi[];
  state: BookingState;
  state_display: string;
  transfer_type: TransferType;
  transfer_type_display: string;
  type_of_trip: TypeOfTrip;
  type_of_trip_display: string;
  uuid: string;
}

export interface IBookingDetails {
  agency: string;
  agencyFk: {
    agencyId: number;
    completeName: string;
    id: string;
    shortName: string;
  } | null;
  attachments: {
    attachmentLink: string;
    isPassport: boolean;
  }[];
  bookingAgentEmail: string;
  bookingAgentFirstName: string;
  bookingAgentLastName: string;
  bookingAgentUsername: string;
  dispatchDate: string;
  drives: IBookingDrive[];
  email: string;
  facilityId: string;
  firstName: string;
  flightArrivalDepartureTime: string;
  flightNumber: string;
  forwarded: boolean;
  id: string;
  indexNumber: string;
  lastName: string;
  managerEmail: string;
  pax: number;
  passengersInfo: {
    agency: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    requestingUnit: string;
  }[];
  phoneNumber: string;
  purpose: string;
  purposeDisplay: string;
  recurringData: {
    endDate: string;
    frequencyDisplay: string;
    startDate: string;
    weekdaysDisplay: string;
  } | null;
  remarks: string;
  requestingUnit: string;
  requestingUnitDisplay: string;
  routes: IBookingRoute[];
  state: BookingState;
  stateDisplay: string;
  transferType: TransferType;
  transferTypeDisplay: string;
  typeOfTrip: TypeOfTrip;
  typeOfTripDisplay: string;
}

export interface IBookingListApi {
  agency: string;
  agency_fk: string;
  agency_short_name: string;
  dispatch_date: string;
  first_name: string;
  forward_key: BookingForwardType;
  forward_label: string;
  is_recurring: boolean;
  last_name: string;
  number_of_pax: string;
  pickup_date: string;
  pickup_time: string;
  requesting_unit: string;
  state: BookingState;
  transfer_type: TransferType;
  type_of_trip: TypeOfTrip;
  uuid: string;
}

export interface IBookingList {
  agencyFk: string;
  agencyName: string;
  agencyShortName: string;
  dispatchDate: string;
  firstName: string;
  forwardKey: BookingForwardType;
  forwardLabel: string;
  id: string;
  isRecurring: boolean;
  lastName: string;
  pax: string;
  pickupDate: string;
  pickupTime: string;
  requestingUnit: string;
  state: BookingState;
  transferType: TransferType;
  typeOfTrip: TypeOfTrip;
}

export interface IBookingUpdate {
  comment?: string;
  state?: BookingState;
}

export interface IFilters {
  passenger_agencies: IDropdownOption[];
}

export interface IBookingDriveApi {
  driver: string;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string;
  dropoff_town: string;
  is_active: boolean;
  is_carpooled: boolean;
  is_ride_shared: boolean;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  signature_code: string;
  uuid: string;
  vehicle: string;
}

export interface IBookingDrive {
  driveNumber: number;
  driver: string;
  dropoffDate: string;
  dropoffLocation: string;
  dropoffTime: string;
  dropoffTown: string;
  id: string;
  isActive: boolean;
  isCarpooled: boolean;
  isRideShared: boolean;
  pickupDate: string;
  pickupLocation: string;
  pickupTime: string;
  pickupTown: string;
  signatureCode: string;
  vehicle: string;
}

export interface IBookingRouteApi {
  drive_id: string;
  dropoff_date: string;
  dropoff_location: string;
  dropoff_time: string;
  dropoff_town: string;
  pickup_date: string;
  pickup_location: string;
  pickup_time: string;
  pickup_town: string;
  signature_code: string;
  uuid: string;
}

export interface IBookingRoute {
  driveId: string;
  dropoffDate: string;
  dropoffLocation: string;
  dropoffTime: string;
  dropoffTown: string;
  id: string;
  isActive?: boolean;
  pickupDate: string;
  pickupLocation: string;
  pickupTime: string;
  pickupTown: string;
  routeNumber?: number;
  signatureCode: string;
}
